// src/services/teamsService.ts
import * as microsoftTeams from "@microsoft/teams-js";

interface TeamsContext {
    isInTeams: boolean;
    context: microsoftTeams.app.Context | null;
    token: string | null;
}

class TeamsAuthService {
    private teamsContext: TeamsContext = {
        isInTeams: false,
        context: null,
        token: null,
    };

    private initializationPromise: Promise<void> | null = null;
    private isInitialized = false;

    /**
     * Initialize Teams SDK and check if running in Teams
     */
    async initialize(): Promise<TeamsContext> {
        if (this.isInitialized) {
            return this.teamsContext;
        }

        if (this.initializationPromise) {
            await this.initializationPromise;
            return this.teamsContext;
        }

        this.initializationPromise = this._initialize();
        await this.initializationPromise;
        return this.teamsContext;
    }

    private async _initialize(): Promise<void> {
        // Teams always embeds in an iframe; top-level window can't be Teams, so skip SDK init to avoid the validDomains.json fetch.
        if (typeof window !== "undefined" && window.parent === window.self) {
            this.teamsContext.isInTeams = false;
            this.isInitialized = true;
            return;
        }
        try {
            // Try to initialize Teams - will fail if not in Teams context
            await microsoftTeams.app.initialize();

            const context = await microsoftTeams.app.getContext();

            this.teamsContext.isInTeams = true;
            this.teamsContext.context = context;
            this.isInitialized = true;
        } catch (error) {
            // Not running in Teams - this is expected and OK
            this.teamsContext.isInTeams = false;
            this.isInitialized = true;
        }
    }

    /**
     * Get SSO token from Teams
     */
    async getTeamsToken(): Promise<string | null> {
        if (!this.teamsContext.isInTeams) {
            return null;
        }

        // Return cached token if available and not expired
        if (this.teamsContext.token) {
            if (!this.isTokenExpired(this.teamsContext.token)) {
                return this.teamsContext.token;
            }
        }

        return new Promise((resolve, reject) => {
            microsoftTeams.authentication.getAuthToken({
                successCallback: (token) => {
                    this.teamsContext.token = token;
                    resolve(token);
                },
                failureCallback: (error) => {
                    console.error("Teams SSO failed:", error);
                    this.teamsContext.token = null;
                    reject(error);
                },
            });
        });
    }

    /**
     * Check if JWT token is expired
     */
    private isTokenExpired(token: string): boolean {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const expirationTime = payload.exp * 1000; // Convert to milliseconds
            const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
            return Date.now() >= (expirationTime - bufferTime);
        } catch (error) {
            console.error("Error parsing token:", error);
            return true;
        }
    }

    /**
     * Get the current Teams context
     */
    getContext(): TeamsContext {
        return this.teamsContext;
    }

    /**
     * Check if running in Teams
     */
    isRunningInTeams(): boolean {
        return this.teamsContext.isInTeams;
    }

    /**
     * Notify Teams that app is ready (call after app loads)
     */
    notifyAppLoaded(): void {
        if (this.teamsContext.isInTeams) {
            microsoftTeams.app.notifySuccess();
        }
    }
}

// Export singleton instance
export const teamsAuthService = new TeamsAuthService();